import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@trillion/auth";
import { SignInForm } from "@trillion/ui/components/sign-in-form";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardForm,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { getEnv } from "@trillion/ui/lib/env";
import { safeLoginReturnUrl } from "../lib/login-return-url";

function parseClientIdFromReturnUrl(returnUrl: string | null): string | null {
  if (!returnUrl) return null;
  try {
    const url = new URL(returnUrl, window.location.origin);
    const clientId = url.searchParams.get("client_id");
    return clientId && clientId.length > 0 ? clientId : null;
  } catch {
    return null;
  }
}

// NOTE: The `returnUrl` query param read by this page is NOT the SSO returnUrl
// that was removed in sc-85339. This is the OpenIddict /authorize ← passport-web
// /login bounce: Passport's own /authorize endpoint (AuthorizationController in
// passport-api) redirects unauthenticated users to /login?returnUrl=/authorize?...
// so that, after they sign in here, this page can navigate the browser back to
// /authorize and let OpenIddict complete the SSO dance. The two `returnUrl`s
// look the same but solve different problems — don't conflate them by adding
// ?returnUrl= handling to @trillion/auth's loginWithPassport().
export function LoginPage() {
  const { login, verifyMfa, challengeMfa } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorParam = searchParams.get("error");
  const returnUrl = searchParams.get("returnUrl");
  const clientIdFromError = searchParams.get("client_id");
  const clientIdFromReturn = parseClientIdFromReturnUrl(returnUrl);
  const clientId = clientIdFromError ?? clientIdFromReturn;

  const serverRejected = errorParam === "invalid_client";

  const [productName, setProductName] = useState<string | null>(null);
  const [clientNotFound, setClientNotFound] = useState(false);

  // Second-factor step. When the password step returns mfaRequired, we hold the (rolling) mfa_token and switch
  // the card to the OTP form. Resend swaps in the fresh token the server re-mints.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!clientId || serverRejected) return;

    const apiUrl = getEnv("PASSPORT_API_URL");
    if (!apiUrl) return;

    const controller = new AbortController();
    fetch(`${apiUrl}/api/clients/${encodeURIComponent(clientId)}/display`, {
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 404) {
          setClientNotFound(true);
          return;
        }
        if (!res.ok) return;
        const data = (await res.json()) as { display_name?: string | null };
        if (data.display_name) setProductName(data.display_name);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          console.warn("Failed to fetch client display name", err);
        }
      });

    return () => controller.abort();
  }, [clientId, serverRejected]);

  function navigateAfterLogin() {
    // Default to /account — the only authenticated page. "/" hard-redirects to /login, so defaulting there
    // bounces a just-logged-in user straight back to the login form. returnUrl (the SSO /authorize bounce)
    // still wins when present.
    const target = safeLoginReturnUrl(returnUrl, getEnv("PASSPORT_API_URL"));
    // If returnUrl is absolute (e.g., OAuth /authorize at Passport API), do a full-page navigation
    // so the browser leaves passport-web entirely. Otherwise use React Router's internal navigation.
    if (/^https?:\/\//i.test(target)) {
      window.location.href = target;
    } else {
      navigate(target, { replace: true });
    }
  }

  async function handleSubmit(email: string, password: string) {
    const result = await login(email, password);
    if (result.mfaRequired) {
      // Don't navigate — switch to the OTP step. (login() did NOT establish a session.)
      setMfaToken(result.mfaToken);
      return;
    }
    navigateAfterLogin();
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setSubmitting(true);
    try {
      await verifyMfa(mfaToken, useRecovery ? { recoveryCode } : { code });
      navigateAfterLogin();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!mfaToken) return;
    try {
      const fresh = await challengeMfa(mfaToken);
      setMfaToken(fresh);
      setCode("");
      toast.success("A new code has been sent to your email.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend the code");
    }
  }

  const isInvalidClient = serverRejected || clientNotFound;
  const title = isInvalidClient ? "Passport" : (productName ?? "Passport");
  const description = isInvalidClient
    ? undefined
    : productName
      ? `Sign in to continue to ${productName}`
      : "Enter your email and password to access your account";
  const errorMessage = isInvalidClient
    ? "This sign-in link is invalid. The application that sent you here is not recognized."
    : null;

  // Second-factor step.
  if (mfaToken) {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Two-factor authentication</CardTitle>
              <CardDescription>
                {useRecovery
                  ? "Enter one of your recovery codes."
                  : "Enter the 6-digit code we emailed you."}
              </CardDescription>
            </CardHeader>
            <CardForm onSubmit={handleMfaSubmit}>
              <CardContent className="space-y-4">
                {useRecovery ? (
                  <div className="space-y-2">
                    <Label htmlFor="recovery_code">Recovery code</Label>
                    <Input
                      id="recovery_code"
                      autoComplete="one-time-code"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value)}
                      placeholder="XXXXX-XXXXX"
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="code">Verification code</Label>
                    <Input
                      id="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      autoFocus
                    />
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Verifying…" : "Verify"}
                </Button>
                <div className="flex w-full items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-muted-foreground underline-offset-4 hover:underline"
                    onClick={() => {
                      setUseRecovery((v) => !v);
                      setCode("");
                      setRecoveryCode("");
                    }}
                  >
                    {useRecovery ? "Use the emailed code" : "Use a recovery code"}
                  </button>
                  {!useRecovery && (
                    <button
                      type="button"
                      className="text-muted-foreground underline-offset-4 hover:underline"
                      onClick={handleResend}
                    >
                      Resend code
                    </button>
                  )}
                </div>
              </CardFooter>
            </CardForm>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <SignInForm
      title={title}
      description={description}
      onSubmit={handleSubmit}
      registerHref={isInvalidClient ? undefined : "/register"}
      registerLabel="Create one"
      forgotPasswordHref={isInvalidClient ? undefined : "/forgot-password"}
      LinkComponent={Link}
      error={errorMessage}
      showLocalForm={!isInvalidClient}
    />
  );
}
